/**
 * When to ask. The short answer: every time they come back, until they install.
 *
 * The first design asked once per device and then backed off — a day, three
 * days, a week. That is the polite schedule, and it is the wrong one here, for
 * two reasons:
 *
 *   1. **Existing users.** Everyone who signed up before this screen existed
 *      has already had their one chance and missed it. A once-per-device rule
 *      keyed on a flag they never set would still have shown them nothing.
 *   2. **The ask is not cosmetic.** On iOS a browser tab cannot receive a
 *      single push notification. Someone who never installs never gets the
 *      reminder, which is the product. Asking once and going quiet is a polite
 *      way to let them keep a broken version of the app.
 *
 * So the unit is the SESSION, not the device: once per app open, every open,
 * until the app is genuinely installed. Within a session it asks once and then
 * stays out of the way as a banner — asking twice in one sitting is nagging,
 * asking once each time they return is a standing offer.
 *
 * Pure, so the rule is testable rather than something you observe over a week.
 */

export interface InstallMemory {
  /** How many times, across all sessions, they have said "not now". */
  dismissals: number;
  /** The last time we asked, epoch ms. Kept for the record and the copy. */
  lastAskedMs: number | null;
  /**
   * They told us they installed it, on a platform where we cannot verify —
   * every manual route, which is all of iOS. It softens the next ask rather
   * than silencing it, because the alternative is trusting a tap over the
   * evidence that they are still, right now, in a browser tab.
   */
  claimedInstalled: boolean;
}

export const NO_MEMORY: InstallMemory = {
  dismissals: 0,
  lastAskedMs: null,
  claimedInstalled: false,
};

/**
 * After this many refusals the copy stops selling and starts acknowledging.
 * Repeating the same pitch to someone who has declined it four times reads as
 * not listening, and the honest line — "still in the browser?" — is shorter.
 */
export const PERSISTENT_AFTER = 3;

/**
 * The full-screen step, once per session.
 *
 * @param askedThisSession whether this app-open has already shown it
 */
export function stepDue(installed: boolean, askedThisSession: boolean): boolean {
  return !installed && !askedThisSession;
}

/**
 * The slim banner on the larder — the standing offer after the step is closed.
 * It stays for the rest of the session, so the way in is never more than a tap
 * away, and it never covers content.
 */
export function nudgeDue(installed: boolean, askedThisSession: boolean): boolean {
  return !installed && askedThisSession;
}

/** They said "not now". */
export function deferred(m: InstallMemory, nowMs: number): InstallMemory {
  return {
    dismissals: m.dismissals + 1,
    lastAskedMs: nowMs,
    // A fresh refusal retracts any earlier claim to have installed it.
    claimedInstalled: false,
  };
}

/** They said they did it — or the browser confirmed it. */
export function claimed(m: InstallMemory, nowMs: number): InstallMemory {
  // Not a refusal, so the counter does not advance: someone who tried and
  // failed should not be met with the worn-down copy next time.
  return { ...m, lastAskedMs: nowMs, claimedInstalled: true };
}

// ── Persistence ───────────────────────────────────────────────────────
//
// localStorage rather than Firestore, on purpose. Being installed is a fact
// about THIS DEVICE, not about the account: syncing it would mean signing in on
// a new phone and never being asked, which is the exact moment the ask is worth
// most. It also means the nudge works before the first Firestore read.

const KEY = "larder.install.v1";
/** sessionStorage: survives a reload, dies with the tab. One ask per open. */
const SESSION_KEY = "larder.install.asked";

export function readMemory(store: Storage | undefined = safeLocal()): InstallMemory {
  if (!store) return NO_MEMORY;
  try {
    const raw = store.getItem(KEY);
    if (!raw) return NO_MEMORY;
    const parsed = JSON.parse(raw) as Partial<InstallMemory>;
    // Field by field: a half-written or older record must not produce an
    // undefined count that silently breaks the copy downstream.
    return {
      dismissals: typeof parsed.dismissals === "number" ? parsed.dismissals : 0,
      lastAskedMs: typeof parsed.lastAskedMs === "number" ? parsed.lastAskedMs : null,
      claimedInstalled: parsed.claimedInstalled === true,
    };
  } catch {
    return NO_MEMORY;
  }
}

export function writeMemory(m: InstallMemory, store: Storage | undefined = safeLocal()) {
  try {
    store?.setItem(KEY, JSON.stringify(m));
  } catch {
    // Private mode and storage-full both throw here. Losing the record means
    // being asked again next session — which is the behaviour anyway — while
    // throwing would take the whole screen down.
  }
}

/**
 * Has this app-open already asked?
 *
 * Scoped by uid so that signing out and in as someone else asks again: a shared
 * family phone is a real case, and the second person's device is just as
 * uninstalled as the first person's was.
 */
export function askedThisSession(uid: string, store = safeSession()): boolean {
  try {
    return store?.getItem(`${SESSION_KEY}.${uid}`) === "1";
  } catch {
    return false;
  }
}

export function markAskedThisSession(uid: string, store = safeSession()) {
  try {
    store?.setItem(`${SESSION_KEY}.${uid}`, "1");
  } catch {
    // Storage refused. The cost is showing the step again on the next render,
    // which the caller's own state already guards against within a session.
  }
}

/** Reading either store throws outright when cookies are blocked. */
function safeLocal(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

function safeSession(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}
